/**
 * EL TABLERO DE DECISIONES DE JOSSE, sin DOM y sin red (DECISIONES-ARTEFACTO-T1, Ares #25843).
 *
 * DE DONDE SALE (T30, Josse): la pantalla de decisiones no ordenaba por fecha, no filtraba y
 * su contador no distinguia respondida de implementada. Medido el 12-oct en la base: 4
 * pendientes (D-104..D-107) y 98 respondidas; la vista solo mostraba las 4 pendientes.
 *
 * LA FUENTE es GET /api/v1/decisiones (DecisionesJosseService, NexoAPI main 25bda2e): devuelve
 * todas con ?estado= vacio, ORDER BY creada_el DESC, hasta 200.
 *
 * IMPLEMENTADA NO SE ADIVINA. Implementada = respondida Y la tarea ligada en completed. El
 * endpoint hoy NO trae el estado de esa tarea (ni su id), y el panel no puede buscar una tarea
 * por codigo. Asi que se lee `tareaEstado` si la API lo manda (contrato pedido a A) y, mientras
 * no lo mande, la respondida dice "implementacion sin medir" en vez de inventar un si o un no.
 * Medido el 12-oct: de 98 respondidas, 50 tienen task_code, 49 encuentran su tarea y 29 de esas
 * estan completed.
 *
 * Lo prueba scripts/check-tablero-decisiones.mjs.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v));
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

export const ESTADOS = ['pendiente', 'respondida', 'implementada'];

/** Quien pidio, en palabras. El codigo se conserva para el filtro. */
export function nombreDe(codigo) {
  const c = texto(codigo).trim();
  if (/^ARES/i.test(c)) return 'Ares';
  if (/^AGT-WIZARD/i.test(c) || /^josse/i.test(c)) return 'Josse';
  return c || 'sin dato';
}

/** Las opciones en las dos formas que hay en la tabla: {clave,texto} y {Clave,Texto,Recomendada}. */
export function leerOpciones(op) {
  let lista = op;
  if (typeof lista === 'string') { try { lista = JSON.parse(lista); } catch { lista = []; } }
  if (!Array.isArray(lista)) return [];
  return lista.map((o) => (typeof o === 'string'
    ? { clave: '', texto: o, recomendada: false }
    : {
      clave: texto(pick(o, 'clave', 'Clave')),
      texto: texto(pick(o, 'texto', 'Texto')),
      recomendada: pick(o, 'recomendada', 'Recomendada') === true
    })).filter((o) => o.texto || o.clave);
}

/** Una decision de la API, con los nombres que usa la vista. Tolera camelCase y PascalCase. */
export function leerDecision(d) {
  const estadoBase = texto(pick(d, 'estado', 'Estado')).toLowerCase();
  const tareaEstado = pick(d, 'tareaEstado', 'TareaEstado', 'taskStatus', 'TaskStatus');
  return {
    codigo: texto(pick(d, 'codigo', 'Codigo')),
    titulo: texto(pick(d, 'titulo', 'Titulo')),
    pregunta: texto(pick(d, 'pregunta', 'Pregunta')),
    opciones: leerOpciones(pick(d, 'opciones', 'Opciones')),
    pedidaPor: texto(pick(d, 'pedidaPor', 'PedidaPor', 'pedida_por')),
    mision: texto(pick(d, 'missionCode', 'MissionCode', 'mission_code')),
    tarea: texto(pick(d, 'taskCode', 'TaskCode', 'task_code')),
    bitacoraId: pick(d, 'bitacoraId', 'BitacoraId', 'bitacora_id'),
    estadoBase,
    vencida: pick(d, 'vencida', 'Vencida') === true,
    respuesta: texto(pick(d, 'respuesta', 'Respuesta')),
    respuestaClave: texto(pick(d, 'respuestaClave', 'RespuestaClave', 'respuesta_clave')),
    respondidaEl: texto(pick(d, 'respondidaEl', 'RespondidaEl', 'respondida_el')),
    respondidaPor: texto(pick(d, 'respondidaPor', 'RespondidaPor', 'respondida_por')),
    // Por donde llego la respuesta (chat, bitacora...): Ares #25944, D-106 llego por voz via Argemiro O.
    canal: texto(pick(d, 'canal', 'Canal')),
    creadaEl: texto(pick(d, 'creadaEl', 'CreadaEl', 'creada_el')),
    // null = la API no lo dijo (no se midio); '' no se usa.
    tareaEstado: tareaEstado === null ? null : texto(tareaEstado).toLowerCase()
  };
}

/**
 * El estado que se muestra. Implementada solo si la tarea ligada esta completed y la API lo dijo.
 * @returns {{ estado: 'pendiente'|'respondida'|'implementada', implementacion: 'medida'|'sin_medir'|'sin_tarea'|null }}
 */
export function estadoDe(d) {
  if (d.estadoBase === 'pendiente') return { estado: 'pendiente', implementacion: null };
  if (!d.tarea) return { estado: 'respondida', implementacion: 'sin_tarea' };
  if (d.tareaEstado === null) return { estado: 'respondida', implementacion: 'sin_medir' };
  return d.tareaEstado === 'completed'
    ? { estado: 'implementada', implementacion: 'medida' }
    : { estado: 'respondida', implementacion: 'medida' };
}

const ms = (iso) => { const t = new Date(iso).getTime(); return Number.isNaN(t) ? 0 : t; };

/** Pendientes arriba (las mas nuevas primero); despues el resto por la fecha de su respuesta, recientes primero. */
export function ordenar(lista) {
  return [...lista].sort((a, b) => {
    const pa = a.estadoBase === 'pendiente' ? 0 : 1;
    const pb = b.estadoBase === 'pendiente' ? 0 : 1;
    if (pa !== pb) return pa - pb;
    const fa = ms(a.respondidaEl || a.creadaEl);
    const fb = ms(b.respondidaEl || b.creadaEl);
    if (fa !== fb) return fb - fa;
    return b.codigo.localeCompare(a.codigo, 'es', { numeric: true });
  });
}

/** filtro.estado: 'todas' | 'pendiente' | 'respondida' | 'implementada'; filtro.pedidaPor: '' = todos. */
export function filtrar(lista, filtro = {}) {
  const est = filtro.estado || 'todas';
  const quien = texto(filtro.pedidaPor);
  return lista.filter((d) => (est === 'todas' || estadoDe(d).estado === est) && (!quien || d.pedidaPor === quien));
}

/** Cuantas de cada clase, sobre la lista entera (no la filtrada). */
export function contar(lista) {
  const c = { total: lista.length, pendiente: 0, respondida: 0, implementada: 0, sinMedir: 0 };
  for (const d of lista) {
    const e = estadoDe(d);
    c[e.estado]++;
    if (e.implementacion === 'sin_medir') c.sinMedir++;
  }
  return c;
}

/** Quienes pidieron, para el filtro: [{ codigo, nombre, n }] de mas a menos. */
export function quienesPidieron(lista) {
  const m = new Map();
  for (const d of lista) m.set(d.pedidaPor, (m.get(d.pedidaPor) || 0) + 1);
  return [...m.entries()].map(([codigo, n]) => ({ codigo, nombre: nombreDe(codigo), n })).sort((a, b) => b.n - a.n);
}

/** El contador del Dashboard: las pendientes reales. null si no se pudo leer (no se inventa un cero). */
export function pendientesReales(respuestaOk, lista) {
  if (!respuestaOk || !Array.isArray(lista)) return null;
  return lista.filter((d) => d.estadoBase === 'pendiente').length;
}
